import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import RouteFallback from "@/components/RouteFallback";
import CookieNotice from "@/components/legal/CookieNotice";

// All pages are code-split (React.lazy) so the initial bundle stays small.
// Auth / misc pages
const LoginPage = lazy(() => import("@/pages/LoginPage.jsx"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage.jsx"));
const PendingApprovalPage = lazy(() => import("@/pages/PendingApprovalPage.jsx"));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePasswordPage.jsx"));
const SuspendedPage = lazy(() => import("@/pages/SuspendedPage.jsx"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage.jsx"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage.jsx"));

// Artist Pages
const ArtistLayout = lazy(() => import("@/layouts/ArtistLayout.jsx"));
const ArtistDashboard = lazy(() => import("@/pages/artist/Dashboard.jsx"));
const ReleasesPage = lazy(() => import("@/pages/artist/Releases.jsx"));
const SingleReleaseWizard = lazy(() => import("@/pages/artist/SingleReleaseWizard.jsx"));
const AlbumReleaseWizard = lazy(() => import("@/pages/artist/AlbumReleaseWizard.jsx"));
const ReleaseEditRouter = lazy(() => import("@/pages/artist/ReleaseEditRouter.jsx"));
const ReleaseDetailPage = lazy(() => import("@/pages/artist/ReleaseDetail.jsx"));
const PayoutsPage = lazy(() => import("@/pages/artist/Payouts.jsx"));
const SupportPage = lazy(() => import("@/pages/artist/Support.jsx"));
const TicketDetailPage = lazy(() => import("@/pages/artist/TicketDetail.jsx"));
const NewTicketPage = lazy(() => import("@/pages/artist/NewTicket.jsx"));
const AnalyticsPage = lazy(() => import("@/pages/artist/Analytics.jsx"));
const ReferralsPage = lazy(() => import("@/pages/artist/Referrals.jsx"));
const ProfilePage = lazy(() => import("@/pages/artist/Profile.jsx"));
const SpotifyAccessPage = lazy(() => import("@/pages/artist/SpotifyAccess.jsx"));
const SubscriptionPage = lazy(() => import("@/pages/artist/Subscription.jsx"));
const SplitsPage = lazy(() => import("@/pages/artist/Splits.jsx"));
const PromotionsPage = lazy(() => import("@/pages/artist/Promotions.jsx"));
const MasteringPage = lazy(() => import("@/pages/artist/Mastering.jsx"));
const MasteringHistory = lazy(() => import("@/pages/artist/MasteringHistory.jsx"));
const AIMasteringPage = lazy(() => import("@/pages/artist/AIMastering.jsx"));

// Feature Pages
const PlaylistExplorer = lazy(() => import("@/pages/artist/PlaylistExplorer.jsx"));
const AIPlaylistMatcher = lazy(() => import("@/pages/artist/AIPlaylistMatcher.jsx"));
const PlaylistCampaigns = lazy(() => import("@/pages/artist/PlaylistCampaigns.jsx"));
const BioLinkPage = lazy(() => import("@/pages/artist/BioLink.jsx"));
const CanvasStudioPage = lazy(() => import("@/pages/artist/CanvasStudio.jsx"));
const AIVideoGenerator = lazy(() => import("@/pages/artist/AIVideoGenerator.jsx"));
const AIStudioPage = lazy(() => import("@/pages/artist/AIStudio.jsx"));
const LyricsManagerPage = lazy(() => import("@/pages/artist/LyricsManager.jsx"));
const ContributorManagerPage = lazy(() => import("@/pages/artist/ContributorManager.jsx"));
const AIMusicAdvisor = lazy(() => import("@/pages/artist/AIMusicAdvisor.jsx"));
const AudioTools = lazy(() => import("@/pages/artist/AudioTools.jsx"));
const ArtistConnections = lazy(() => import("@/pages/artist/ArtistConnections.jsx"));
const QRCodeGenerator = lazy(() => import("@/pages/artist/QRCodeGenerator.jsx"));
const SmartLinksPage = lazy(() => import("@/pages/artist/SmartLinks.jsx"));
const PreSavePage = lazy(() => import("@/pages/artist/PreSave.jsx"));
const LyricsSyncPage = lazy(() => import("@/pages/artist/LyricsSync.jsx"));
const CollaborationsPage = lazy(() => import("@/pages/artist/Collaborations.jsx"));
const NotificationsPage = lazy(() => import("@/pages/artist/Notifications.jsx"));

// Public Pages (no auth required)
const SmartLinkPage = lazy(() => import("@/pages/SmartLinkPage.jsx"));
const PublicPreSavePage = lazy(() => import("@/pages/PublicPreSavePage.jsx"));
const PublicBioPage = lazy(() => import("@/pages/PublicBioPage.jsx"));
const SupportHubPage = lazy(() => import("@/pages/SupportHub.jsx"));
const LegalPage = lazy(() => import("@/pages/LegalPage.jsx"));

// Admin Pages
const AdminLayout = lazy(() => import("@/layouts/AdminLayout.jsx"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard.jsx"));
const AdminReviewQueue = lazy(() => import("@/pages/admin/ReviewQueue.jsx"));
const AdminReleaseDetail = lazy(() => import("@/pages/admin/ReleaseDetail.jsx"));
const AdminArtists = lazy(() => import("@/pages/admin/Artists.jsx"));
const AdminPayouts = lazy(() => import("@/pages/admin/Payouts.jsx"));
const AdminPayoutMethods = lazy(() => import("@/pages/admin/PayoutMethods.jsx"));
const AdminSupport = lazy(() => import("@/pages/admin/Support.jsx"));
const AdminTicketDetail = lazy(() => import("@/pages/admin/TicketDetail.jsx"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/Announcements.jsx"));
const AdminYouTubeStats = lazy(() => import("@/pages/admin/YouTubeStats.jsx"));
const AdminSpotifyStats = lazy(() => import("@/pages/admin/SpotifyStats.jsx"));
const AdminArtistSpotifyManager = lazy(() => import("@/pages/admin/ArtistSpotifyManager.jsx"));
const AdminSplits = lazy(() => import("@/pages/admin/Splits.jsx"));
const AdminCampaigns = lazy(() => import("@/pages/admin/Campaigns.jsx"));
const AdminPlaylistCampaigns = lazy(() => import("@/pages/admin/PlaylistCampaigns.jsx"));
const AdminAIMatcher = lazy(() => import("@/pages/admin/AIMatcher.jsx"));
const AdminFeatureManager = lazy(() => import("@/pages/admin/FeatureManager.jsx"));
const AdminPlaylists = lazy(() => import("@/pages/admin/Playlists.jsx"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/Subscriptions.jsx"));
const AdminReferrals = lazy(() => import("@/pages/admin/Referrals.jsx"));
const AdminContentManagement = lazy(() => import("@/pages/admin/ContentManagement.jsx"));
const AdminEarnings = lazy(() => import("@/pages/admin/Earnings.jsx"));

const isAdminRole = (user) => user?.role === "admin" || user?.role === "superadmin";

/** Child route content inside a layout: keeps the sidebar/header mounted while the page chunk loads. */
const page = (Component) => (
  <Suspense fallback={<RouteFallback variant="content" />}>
    <Component />
  </Suspense>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], requireApproved = true }) => {
  const { user, token, loading } = useAuth();

  if (loading) return <RouteFallback />;

  // Not authenticated - no token / user not loaded
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to change password
  if (user.force_password_change) {
    return <Navigate to="/change-password" replace />;
  }

  // Check if user is pending approval
  if (requireApproved && user.status === "pending_approval") {
    return <Navigate to="/pending-approval" replace />;
  }

  // Check if user is suspended
  if (user.status === "suspended_license") {
    return <Navigate to="/suspended" replace />;
  }

  // Check role - admin on artist routes -> /eadmin, artist on admin routes -> /
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={isAdminRole(user) ? "/eadmin" : "/"} replace />;
  }

  return children;
};

// Auth-only guard (no status/role checks) for /pending-approval, /change-password, /suspended
const RequireAuth = ({ children }) => {
  const { user, token, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (!token || !user) return <Navigate to="/login" replace />;
  return children;
};

// Public Route - redirect authenticated users away from login/register
const PublicRoute = ({ children }) => {
  const { user, token, loading } = useAuth();

  if (loading) return <RouteFallback />;

  if (token && user) {
    if (user.force_password_change) {
      return <Navigate to="/change-password" replace />;
    }
    if (user.status === "pending_approval") {
      return <Navigate to="/pending-approval" replace />;
    }
    return <Navigate to={isAdminRole(user) ? "/eadmin" : "/"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register/:inviteCode" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pending-approval" element={<RequireAuth><PendingApprovalPage /></RequireAuth>} />
        <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
        <Route path="/suspended" element={<RequireAuth><SuspendedPage /></RequireAuth>} />

        {/* Public Support Hub (no auth) */}
        <Route path="/help" element={<SupportHubPage />} />
        <Route path="/help/:categorySlug" element={<SupportHubPage />} />
        <Route path="/help/:categorySlug/:articleSlug" element={<SupportHubPage />} />

        {/* Public legal pages (no auth) */}
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/cookies" element={<LegalPage doc="cookies" />} />

        {/* Artist Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={["artist"]}>
              <ArtistLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={page(ArtistDashboard)} />
          <Route path="releases" element={page(ReleasesPage)} />
          <Route path="releases/new" element={page(SingleReleaseWizard)} />
          <Route path="releases/new/album" element={page(AlbumReleaseWizard)} />
          <Route path="releases/:id" element={page(ReleaseDetailPage)} />
          <Route path="releases/:id/edit" element={page(ReleaseEditRouter)} />
          <Route path="releases/:id/edit/single" element={page(SingleReleaseWizard)} />
          <Route path="releases/:id/edit/album" element={page(AlbumReleaseWizard)} />
          <Route path="payouts" element={page(PayoutsPage)} />
          <Route path="payout-methods" element={<Navigate to="/payouts" replace />} />
          <Route path="support" element={page(SupportPage)} />
          <Route path="support/new" element={page(NewTicketPage)} />
          <Route path="support/:id" element={page(TicketDetailPage)} />
          <Route path="analytics" element={page(AnalyticsPage)} />
          <Route path="referrals" element={page(ReferralsPage)} />
          <Route path="profile" element={page(ProfilePage)} />
          <Route path="spotify-access" element={page(SpotifyAccessPage)} />
          <Route path="subscription" element={page(SubscriptionPage)} />
          <Route path="splits" element={page(SplitsPage)} />
          <Route path="promotions" element={page(PromotionsPage)} />
          <Route path="kyc" element={<Navigate to="/payouts" replace />} />
          <Route path="mastering" element={page(MasteringPage)} />
          <Route path="mastering/history" element={page(MasteringHistory)} />
          <Route path="ai-mastering" element={page(AIMasteringPage)} />
          {/* Feature Routes */}
          <Route path="playlist-explorer" element={page(PlaylistExplorer)} />
          <Route path="ai-playlist-matcher" element={page(AIPlaylistMatcher)} />
          <Route path="playlist-campaigns" element={page(PlaylistCampaigns)} />
          <Route path="bio-link" element={page(BioLinkPage)} />
          <Route path="canvas-studio" element={page(CanvasStudioPage)} />
          <Route path="ai-video" element={page(AIVideoGenerator)} />
          <Route path="ai-studio" element={page(AIStudioPage)} />
          <Route path="lyrics" element={page(LyricsManagerPage)} />
          <Route path="contributors" element={page(ContributorManagerPage)} />
          <Route path="ai-advisor" element={page(AIMusicAdvisor)} />
          <Route path="audio-tools" element={page(AudioTools)} />
          <Route path="connections" element={page(ArtistConnections)} />
          <Route path="qr-codes" element={page(QRCodeGenerator)} />
          <Route path="smart-links" element={page(SmartLinksPage)} />
          <Route path="pre-save" element={page(PreSavePage)} />
          <Route path="lyrics-sync" element={page(LyricsSyncPage)} />
          <Route path="collaborations" element={page(CollaborationsPage)} />
          <Route path="notifications" element={page(NotificationsPage)} />
        </Route>

        {/* Public Smart Link Page */}
        <Route path="/s/:shortCode" element={<SmartLinkPage />} />

        {/* Public Pre-Save Page */}
        <Route path="/p/:campaignId" element={<PublicPreSavePage />} />

        {/* Public Bio Page (vanity slug) */}
        <Route path="/u/:slug" element={<PublicBioPage />} />

        {/* Admin Routes - /eadmin */}
        <Route
          path="/eadmin"
          element={
            <ProtectedRoute allowedRoles={["admin", "superadmin"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={page(AdminDashboard)} />
          <Route path="review" element={page(AdminReviewQueue)} />
          <Route path="releases/:id" element={page(AdminReleaseDetail)} />
          <Route path="artists" element={page(AdminArtists)} />
          <Route path="payouts" element={page(AdminPayouts)} />
          <Route path="payout-methods" element={page(AdminPayoutMethods)} />
          <Route path="support" element={page(AdminSupport)} />
          <Route path="support/:id" element={page(AdminTicketDetail)} />
          <Route path="announcements" element={page(AdminAnnouncements)} />
          <Route path="youtube-stats" element={page(AdminYouTubeStats)} />
          <Route path="spotify-stats" element={page(AdminSpotifyStats)} />
          <Route path="artist-spotify" element={page(AdminArtistSpotifyManager)} />
          <Route path="splits" element={page(AdminSplits)} />
          <Route path="campaigns" element={page(AdminCampaigns)} />
          <Route path="playlist-campaigns" element={page(AdminPlaylistCampaigns)} />
          <Route path="ai-matcher" element={page(AdminAIMatcher)} />
          <Route path="feature-manager" element={page(AdminFeatureManager)} />
          <Route path="playlists" element={page(AdminPlaylists)} />
          <Route path="subscriptions" element={page(AdminSubscriptions)} />
          <Route path="referrals" element={page(AdminReferrals)} />
          <Route path="content" element={page(AdminContentManagement)} />
          <Route path="earnings" element={page(AdminEarnings)} />
        </Route>

        {/* Redirect old /admin to /eadmin */}
        <Route path="/admin/*" element={<Navigate to="/eadmin" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

/** Error boundary that resets itself when the route changes. */
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <RoutedErrorBoundary>
              <AppRoutes />
            </RoutedErrorBoundary>
            <Toaster position="top-right" richColors closeButton />
            <CookieNotice />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
